/**
 * 20. Journal Entry Detail / Editor — header (date, period, currency,
 * source doc), lines grid with debit/credit balance indicator, approval
 * status, post/reverse actions.
 *
 * Base amounts are in the legal entity's functional currency
 * (`functionalCurrencyCode`); transaction amounts in the entry's own
 * `currencyCode`, shown only when the two differ (CUR-1, CUR-2).
 */
import { useParams } from "react-router-dom";
import {
  Card,
  PageHeader,
  Skeleton,
  StatusBadge,
  DataTable,
  Badge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { QueryError } from "@/components/QueryError";
import { useTranslation } from "@/lib/i18n";
import { useApiQuery } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";
import { entryNumberLabel } from "./entry-number";

interface JournalLineRow {
  id: string;
  lineNumber: number;
  accountCode: string;
  accountName: string;
  description: string | null;
  baseDebitAmount: string;
  baseCreditAmount: string;
  transactionDebitAmount: string;
  transactionCreditAmount: string;
}

interface JournalEntryDetail {
  id: string;
  /** NULL until Draft -> Posted allocates one (decision 65). */
  entryNumber: string | null;
  entryDate: string;
  status: "Draft" | "Submitted" | "Approved" | "Posted" | "Reversed" | "Voided";
  /** The transaction currency. */
  currencyCode: string;
  /** The books' currency: what base amounts and totals are in. */
  functionalCurrencyCode: string;
  exchangeRate: string;
  journalCode: string;
  periodName: string;
  reference: string | null;
  narration: string | null;
  sourceDocumentType: string | null;
  sourceDocumentId: string | null;
  reversalOfJournalEntryId: string | null;
  totalDebits: string;
  totalCredits: string;
  configContextId: string | null;
  postedAt: string | null;
  postedBy: string | null;
  lines: JournalLineRow[];
}

export function JournalEntryDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, isPending, error, refetch, isFetching } =
    useApiQuery<JournalEntryDetail>(
      ["finance", "journal-entries", id ?? ""],
      `/finance/journal-entries/${id ?? ""}`,
    );

  if (isPending) return <Skeleton rows={12} />;
  if (error || !data)
    return (
      <QueryError
        error={error}
        retrying={isFetching}
        onRetry={() => void refetch()}
      />
    );

  const balanced = data.totalDebits === data.totalCredits;
  const books = data.functionalCurrencyCode;
  const foreign = data.currencyCode !== books;
  const entryNumber = entryNumberLabel(data.entryNumber, data.status);

  return (
    <div>
      <PageHeader
        title={
          data.entryNumber
            ? `Journal Entry ${data.entryNumber}`
            : "Journal Entry"
        }
        subtitle={`${data.journalCode} · ${data.periodName} · ${formatDate(data.entryDate)}`}
        breadcrumb={["Finance", "Journal Entries", entryNumber]}
        actions={
          <>
            <StatusBadge status={data.status} />
            {/* The entry's actions come with the journal entry editor
                (ED-W6), with reversal through the one reversal and its
                reason (SN-5); until then each says so (UI-HONEST). */}
            {data.status === "Draft" && (
              <PendingButton
                variant="primary"
                reason="journalEditor"
                slice="ED-W6"
              >
                {t("documentActions.submit")}
              </PendingButton>
            )}
            {data.status === "Approved" && (
              <PendingButton
                variant="primary"
                reason="journalEditor"
                slice="ED-W6"
              >
                {t("documentActions.post")}
              </PendingButton>
            )}
            {data.status === "Posted" && (
              <PendingButton
                variant="danger"
                reason="journalEditor"
                slice="ED-W6"
              >
                {t("documentActions.reverse")}
              </PendingButton>
            )}
          </>
        }
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <Card>
          <div className="atlas-stat__label">Debits</div>
          <div className="atlas-stat__value">
            {formatMoney(data.totalDebits, books)}
          </div>
        </Card>
        <Card>
          <div className="atlas-stat__label">Credits</div>
          <div className="atlas-stat__value">
            {formatMoney(data.totalCredits, books)}
          </div>
        </Card>
        <Card>
          <div className="atlas-stat__label">Balance</div>
          <div className="atlas-stat__value">
            <Badge tone={balanced ? "success" : "danger"}>
              {balanced ? "Balanced" : "Unbalanced"}
            </Badge>
          </div>
        </Card>
        <Card>
          <div className="atlas-stat__label">Exchange rate</div>
          <div className="atlas-stat__value atlas-mono">
            {data.exchangeRate}
          </div>
        </Card>
      </div>

      <Card title="Lines">
        <DataTable<JournalLineRow>
          rows={data.lines}
          columns={[
            { key: "lineNumber", label: "#", numeric: true },
            {
              key: "accountCode",
              label: "Account",
              render: (r) => (
                <span className="atlas-mono">{r.accountCode}</span>
              ),
            },
            { key: "accountName", label: "Name" },
            { key: "description", label: "Description" },
            ...(foreign
              ? [
                  {
                    key: "transactionDebitAmount",
                    label: `Debit (${data.currencyCode})`,
                    numeric: true,
                    render: (r: JournalLineRow) =>
                      isZero(r.transactionDebitAmount)
                        ? "—"
                        : formatMoney(
                            r.transactionDebitAmount,
                            data.currencyCode,
                          ),
                  },
                  {
                    key: "transactionCreditAmount",
                    label: `Credit (${data.currencyCode})`,
                    numeric: true,
                    render: (r: JournalLineRow) =>
                      isZero(r.transactionCreditAmount)
                        ? "—"
                        : formatMoney(
                            r.transactionCreditAmount,
                            data.currencyCode,
                          ),
                  },
                ]
              : []),
            {
              key: "baseDebitAmount",
              label: foreign ? `Debit (${books})` : "Debit",
              numeric: true,
              render: (r) =>
                isZero(r.baseDebitAmount)
                  ? "—"
                  : formatMoney(r.baseDebitAmount, books),
            },
            {
              key: "baseCreditAmount",
              label: foreign ? `Credit (${books})` : "Credit",
              numeric: true,
              render: (r) =>
                isZero(r.baseCreditAmount)
                  ? "—"
                  : formatMoney(r.baseCreditAmount, books),
            },
          ]}
        />
      </Card>

      <div className="atlas-grid atlas-grid--2 mt-4">
        <Card title="Reference">
          <dl style={{ margin: 0 }}>
            <dt className="atlas-muted">Source document</dt>
            <dd>
              {data.sourceDocumentType
                ? `${data.sourceDocumentType} · ${data.sourceDocumentId}`
                : "—"}
            </dd>
            <dt className="atlas-muted">Reference</dt>
            <dd>{data.reference ?? "—"}</dd>
            <dt className="atlas-muted">Narration</dt>
            <dd>{data.narration ?? "—"}</dd>
          </dl>
        </Card>
        <Card title="Audit & provenance">
          <dl style={{ margin: 0 }}>
            <dt className="atlas-muted">Config context</dt>
            <dd className="atlas-mono" style={{ fontSize: 13 }}>
              {data.configContextId ?? "—"}
            </dd>
            <dt className="atlas-muted">Posted at</dt>
            <dd>{data.postedAt ? formatDate(data.postedAt) : "—"}</dd>
            <dt className="atlas-muted">Posted by</dt>
            <dd>{data.postedBy ?? "—"}</dd>
            <dt className="atlas-muted">Reversal of</dt>
            <dd>{data.reversalOfJournalEntryId ?? "—"}</dd>
          </dl>
        </Card>
      </div>
    </div>
  );
}

/** "0", "0.000000" and the like: an empty side of a journal line. */
function isZero(amount: string | null | undefined): boolean {
  return !amount || /^-?0+(\.0+)?$/.test(amount);
}
